import React, { useEffect, useState, useCallback } from 'react';

const LAT = 39.927;
const LON = -76.886;

const WMO_ICONS = {
  0:  '☀️', 1: '🌤️', 2: '⛅', 3: '☁️',
  45: '🌫️', 48: '🌫️',
  51: '🌦️', 53: '🌦️', 55: '🌧️',
  61: '🌧️', 63: '🌧️', 65: '🌧️',
  71: '🌨️', 73: '🌨️', 75: '❄️',
  77: '❄️',
  80: '🌦️', 81: '🌧️', 82: '⛈️',
  85: '🌨️', 86: '❄️',
  95: '⛈️', 96: '⛈️', 99: '⛈️',
};

const DAY_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function wmoIcon(code) {
  return WMO_ICONS[code] ?? '🌡️';
}

export default function Weather({ compact = false }) {
  const [forecast, setForecast] = useState(null);

  const load = useCallback(async () => {
    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}` +
        `&daily=weathercode,temperature_2m_max,temperature_2m_min` +
        `&temperature_unit=fahrenheit&timezone=America%2FNew_York&forecast_days=3`;
      const r = await fetch(url);
      if (!r.ok) throw new Error();
      const data = await r.json();
      const days = data.daily.time.map((dateStr, i) => ({
        date: new Date(dateStr + 'T12:00:00'),
        code: data.daily.weathercode[i],
        hi: Math.round(data.daily.temperature_2m_max[i]),
        lo: Math.round(data.daily.temperature_2m_min[i]),
      }));
      setForecast(days);
    } catch {
      // silently fail — weather is non-critical
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  if (!forecast) return null;

  // Compact: just today's icon + hi/lo for mobile header
  if (compact) {
    const today = forecast[0];
    return (
      <div style={styles.compact}>
        <span style={styles.compactIcon}>{wmoIcon(today.code)}</span>
        <div style={styles.compactTemps}>
          <span style={styles.compactHi}>{today.hi}°</span>
          <span style={styles.compactLo}>{today.lo}°</span>
        </div>
      </div>
    );
  }

  // Full: 3-day forecast for desktop header
  return (
    <div style={styles.wrap}>
      {forecast.map((day, i) => (
        <div key={day.date.toISOString()} style={{
          ...styles.day,
          ...(i === 0 ? styles.dayToday : {}),
        }}>
          <span style={styles.dayLabel}>
            {i === 0 ? 'Today' : DAY_SHORT[day.date.getDay()]}
          </span>
          <span style={styles.icon}>{wmoIcon(day.code)}</span>
          <div style={styles.temps}>
            <span style={styles.hi}>{day.hi}°</span>
            <span style={styles.lo}>{day.lo}°</span>
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  day: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '4px 10px',
    borderRadius: 'var(--radius-sm)',
    minWidth: 54,
  },
  dayToday: {
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
  },
  dayLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.6rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--text-dimmer)',
  },
  icon: {
    fontSize: '1.3rem',
    lineHeight: 1.2,
  },
  temps: {
    display: 'flex',
    gap: 4,
    alignItems: 'baseline',
  },
  hi: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--text)',
    fontWeight: 500,
  },
  lo: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.65rem',
    color: 'var(--text-dimmer)',
  },
  // Compact mobile styles
  compact: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  compactIcon: {
    fontSize: '1.4rem',
    lineHeight: 1,
  },
  compactTemps: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  compactHi: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--text)',
    fontWeight: 500,
    lineHeight: 1.2,
  },
  compactLo: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.62rem',
    color: 'var(--text-dimmer)',
    lineHeight: 1.2,
  },
};
